import keyBy from 'lodash-es/keyBy';
import sum from 'lodash-es/sum';
import {
  ProductClass,
  TradeDirection,
  TradeFlow,
} from '../../graphQL/types/shared';
import {
  formatDistance,
  formatPCI,
  formatRCA,
  formatTradeValue,
} from '../../numberFormatters';
import {
  ICPYDatum,
  IPYDatum,
} from '../../Utils';
import {
  getDisplayedProductCode,
} from '../../Utils';
import {
  DisplayValue,
  DisplayValueStatus,
  IDetailOverlayRow as IRow,
} from '../../viz/Utils';
import {
  IMetadatum,
} from '../fetchedData/productMetadata';
import {
  getKey as get_PY_Key,
} from './convert_PY_DataToMap';
import getTradeLabel from './getTradeLabel';
import {
  IAddTooltipInfoOutput,
  IExportImportDatum,
  INodeWithoutRCAStatus,
  ITooltipDatum,
  LayoutText,
  LayoutTextWithoutRCAStatus,
} from './Utils';

const rcaThreshold = 1;

const addTooltipInfo = (
    nodes: INodeWithoutRCAStatus[],
    texts: LayoutTextWithoutRCAStatus[],
    dataMap: Map<number, ICPYDatum>,
    metadata: Map<number, IMetadatum>,
    data_PY: Map<string, IPYDatum>,
    tradeDirection: TradeDirection,
    tradeFlow: TradeFlow,
    productClass: ProductClass,
    year: number,
    getTradeValue: (input: IExportImportDatum) => number,
  ): IAddTooltipInfoOutput => {

  const tooltipData: ITooltipDatum[] = [];
  const nodesLength = nodes.length;
  for (let i = 0; i < nodesLength; i += 1) {
    const node = nodes[i];
    const {id, radius, x, y, color} = node;
    const {code, name_en, name_short_en, level} = metadata.get(id)!;
    const tradeLabel = getTradeLabel(tradeDirection, tradeFlow);
    const rcaLabel = __lexiconText('applicationWide.rcaFull');
    const distanceLabel = __lexiconText('applicationWide.distance');
    const codeLabel = __lexiconText('applicationWide.code');

    const yearInfo: IRow = {
      label: __lexiconText('applicationWide.year'),
      value: year,
    };
    const datum = dataMap.get(id);
    let tradeInfo: IRow, codeInfo: IRow;
    let rcaInfo: IRow, distanceInfo: IRow, tradeValue: number, pciValue: DisplayValue | string;
    if (datum === undefined) {
      tradeInfo = {
        label: tradeLabel,
        value: DisplayValueStatus.ShowNotAvailable,
      };
      codeInfo = {
        label: codeLabel,
        value: DisplayValueStatus.ShowNotAvailable,
      };
      rcaInfo = {
        label: rcaLabel,
        value: DisplayValueStatus.ShowNotAvailable,
      };
      distanceInfo = {
        label: distanceLabel,
        value: DisplayValueStatus.ShowNotApplicable,
      };
      tradeValue = 0;
      pciValue = {status: DisplayValueStatus.DoNotShow};
    } else {
      const {export_rca, distance} = datum;
      tradeValue = getTradeValue(datum);
      tradeInfo = {
        label: tradeLabel,
        value: formatTradeValue(tradeValue),
      };
      codeInfo = {
        label: codeLabel,
        value: getDisplayedProductCode(code, productClass, level),
      };
      if (export_rca === null) {
        rcaInfo = {
          label: rcaLabel,
          value: {status: DisplayValueStatus.ShowNotApplicable},
        };
      } else {
        rcaInfo = {
          label: rcaLabel,
          value: formatRCA(export_rca),
        };
      }

      if (distance === null) {
        distanceInfo = {
          label: distanceLabel,
          value: {status: DisplayValueStatus.ShowNotApplicable},
        };
      } else {
        distanceInfo = {
          label: distanceLabel,
          value: formatDistance(distance),
        };

      }
      const retrievedDatum_PY = data_PY.get(get_PY_Key(id, year));
      if (retrievedDatum_PY === undefined) {
        pciValue = {status: DisplayValueStatus.ShowNotAvailable};
      } else if (retrievedDatum_PY.pci === null) {
        pciValue = {status: DisplayValueStatus.ShowNotAvailable};
      } else {
        pciValue = formatPCI(retrievedDatum_PY.pci);
      }
    }

    const pciInfo = {label: __lexiconText('applicationWide.pciFull'), value: pciValue};

    const tooltipInfo: IRow[] = [codeInfo, yearInfo, tradeInfo, rcaInfo, distanceInfo, pciInfo];

    const tooltipDatum: ITooltipDatum = {
      id, radius, x, y, color,
      tooltipInfo, code, tradeValue,
      shortLabel: name_short_en,
      longLabel: name_en,
      level,
    };
    tooltipData.push(tooltipDatum);
  }

  const tooltipMap: Record<string, ITooltipDatum> = keyBy(tooltipData, ({id}) => id);
  const total = sum(tooltipData.map(({tradeValue}) => tradeValue));

  const nodesWithRCAStatus = nodes.map(node => {
    const {id} = node;
    const retrieved = dataMap.get(id);
    let isCurrentlyExported: boolean;
    if (retrieved === undefined) {
      isCurrentlyExported = false;
    } else {
      isCurrentlyExported = retrieved.export_rca !== null && retrieved.export_rca >= rcaThreshold;
    }
    return {
      ...node,
      isCurrentlyExported,
    };
  });

  const textsWithRCAStatus: LayoutText[] = texts.map(text => {
    const {id} = text;
    const retrieved = dataMap.get(id);
    let isCurrentlyExported: boolean;
    if (retrieved === undefined) {
      isCurrentlyExported = false;
    } else {
      isCurrentlyExported = retrieved.export_rca !== null && retrieved.export_rca >= rcaThreshold;
    }
    return {
      ...text,
      isCurrentlyExported,
    };
  });
  return {tooltipMap, total, nodes: nodesWithRCAStatus, texts: textsWithRCAStatus};
};

export default addTooltipInfo;
